/**
 * Response validation before anything is written to the Cache API (pure — unit tested).
 *
 * A cached response is served to the viewer/player as if it came from the network, so it must be
 * exactly the file the manifest lists: 200 (never a 206 range or a 304), the API contract header,
 * never HTML (Pages "fail open" index.html), the served Content-Type, and Content-Length / ETag equal
 * to the manifest's size / etag. A missing Content-Length (compressed transfer) is allowed; the
 * downloader then counts the bytes it stores.
 */
export interface HeadersLike {
  get(name: string): string | null;
}

export interface ResponseLike {
  status: number;
  headers: HeadersLike;
}

export interface ExpectedPart {
  size: number;
  etag: string;
  mime: string;
}

export type InvalidReason = 'status' | 'contract' | 'html' | 'type' | 'length' | 'etag';

/** `"abc"` / `W/"abc"` / `abc` → `abc` (null when absent). */
export function normalizeEtag(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim().replace(/^W\//i, '');
  const m = /^"(.*)"$/.exec(t);
  return (m ? m[1]! : t) || null;
}

/** Media type without parameters, lower-cased ('' when absent). */
export function baseType(v: string | null | undefined): string {
  return (v ?? '').split(';')[0]!.trim().toLowerCase();
}

export function validateMediaResponse(res: ResponseLike, expected: ExpectedPart): InvalidReason | null {
  if (res.status !== 200) return 'status';
  if (res.headers.get('x-kgr') !== '1') return 'contract';
  const type = baseType(res.headers.get('content-type'));
  if (type === 'text/html') return 'html';
  if (type !== baseType(expected.mime)) return 'type';
  const len = res.headers.get('content-length');
  if (len !== null && len.trim() !== '' && Number(len) !== expected.size) return 'length';
  if (normalizeEtag(res.headers.get('etag')) !== normalizeEtag(expected.etag)) return 'etag';
  return null;
}

/** Avatars: 200 + contract header + JPEG. */
export function validateAvatarResponse(res: ResponseLike): InvalidReason | null {
  if (res.status !== 200) return 'status';
  if (res.headers.get('x-kgr') !== '1') return 'contract';
  if (baseType(res.headers.get('content-type')) !== 'image/jpeg') return 'type';
  return null;
}
